/*
  Wattec — variant picker cards (snippets/wattec-variant-picker.liquid).

  The cards never talk to the cart themselves. Pressing one sets the theme's
  own variant dropdowns (the <variant-selects> right after the cards) and fires
  their normal change event, so the theme updates the price, picture, cart form
  and floating Add to Cart bar exactly as when a dropdown is used. The cards
  then follow the cart form's variant, however it was changed.

  Also:
  - a choice whose combination is sold out moves to the closest in-stock build
    that keeps it, rather than landing on "Unavailable";
  - each card shows what choosing it does to the price ("+£60", "−£400");
  - the product title (and the floating bar's) names the chosen parts;
  - a visit without ?variant= opens on the variant the title describes.
*/
(function () {
  'use strict';

  function moneyFormatter(currency) {
    var whole, exact;
    try {
      whole = new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency, minimumFractionDigits: 0, maximumFractionDigits: 0 });
      exact = new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch (e) {
      return function (cents) { return (cents / 100).toFixed(2); };
    }
    return function (cents) { return (cents % 100 === 0 ? whole : exact).format(cents / 100); };
  }

  // The theme's variant change also refreshes pickup availability and the
  // gallery, whose scripts load after this one; changing the variant before
  // they're running throws part-way and leaves the cart form on the old
  // variant. So wait for the page's scripts, and for those parts if present
  // (with a time limit, should one of them never load).
  function whenThemeReady(callback) {
    var go = function () {
      var names = ['variant-selects'];
      ['pickup-availability', 'media-gallery'].forEach(function (name) {
        if (document.querySelector(name)) names.push(name);
      });
      var ready = window.customElements && window.Promise
        ? Promise.all(names.map(function (name) { return customElements.whenDefined(name); }))
        : null;
      if (!ready) { callback(); return; }
      var done = false;
      var once = function () { if (!done) { done = true; setTimeout(callback, 0); } };
      ready.then(once);
      setTimeout(once, 4000);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go);
    else go();
  }

  function init(root) {
    if (root.__wtVp) return;
    root.__wtVp = true;

    var sectionId = root.getAttribute('data-section');
    var native = root.nextElementSibling;
    if (!native || native.tagName !== 'VARIANT-SELECTS') {
      native = document.querySelector('variant-selects[data-section="' + sectionId + '"]');
    }
    var dataEl = root.querySelector('[data-wt-vp-data]');
    if (!native || !dataEl) return;

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var variants = data.variants || [];
    var selects = Array.prototype.slice.call(native.querySelectorAll('select'));
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-wt-vp-group]'));
    // Anything unexpected: leave the theme's dropdowns exactly as they were.
    if (!variants.length || !selects.length || selects.length !== groups.length) return;

    var money = moneyFormatter(data.currency || 'GBP');
    var form = document.getElementById('product-form-' + sectionId);
    var idInput = form ? form.querySelector('input[name="id"]') : null;
    var shown = null;

    function byId(id) {
      id = Number(id);
      for (var i = 0; i < variants.length; i++) if (variants[i].id === id) return variants[i];
      return null;
    }
    function byOptions(opts) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i], ok = true;
        for (var k = 0; k < opts.length; k++) if (v.options[k] !== opts[k]) { ok = false; break; }
        if (ok) return v;
      }
      return null;
    }
    // The variant a choice leads to: the exact combination when it's in stock,
    // otherwise the in-stock build that keeps the choice and changes the
    // fewest other parts, otherwise the (sold-out) exact combination.
    function resolve(index, value, base) {
      var want = base.slice();
      want[index] = value;
      var exact = byOptions(want);
      if (exact && exact.available) return exact;
      var best = null, bestScore = -1;
      variants.forEach(function (v) {
        if (v.options[index] !== value || !v.available) return;
        var score = 0;
        v.options.forEach(function (o, k) { if (k !== index && o === base[k]) score++; });
        if (score > bestScore) { best = v; bestScore = score; }
      });
      return best || exact;
    }
    function current() {
      if (idInput && idInput.value) {
        var v = byId(idInput.value);
        if (v) return v;
      }
      return byOptions(selects.map(function (s) { return s.value; }));
    }

    // --- Showing a variant ----------------------------------------------------
    function show(variant) {
      if (!variant) return;
      var changed = !shown || shown.id !== variant.id;
      shown = variant;

      groups.forEach(function (group, k) {
        var chosenValue = variant.options[k];
        var chosen = group.querySelector('[data-wt-vp-chosen]');
        if (chosen && chosen.textContent !== chosenValue) {
          chosen.textContent = chosenValue;
          replay(chosen, 'is-swapped');
        }
        Array.prototype.forEach.call(group.querySelectorAll('.wt-vp__opt'), function (opt) {
          var radio = opt.querySelector('input');
          var value = radio.value;
          var selected = value === chosenValue;
          radio.checked = selected;
          opt.classList.toggle('is-selected', selected);

          // What pressing this card would actually select.
          var candidate = resolve(k, value, variant.options);
          var soldOut = !candidate || !candidate.available;
          opt.classList.toggle('is-soldout', !selected && soldOut);

          var delta = opt.querySelector('[data-wt-vp-delta]');
          var text = '', tone = '';
          if (selected) {
            text = variant.available ? 'Selected' : 'Sold out';
            tone = variant.available ? 'selected' : 'soldout';
          } else if (soldOut) {
            text = 'Sold out';
            tone = 'soldout';
          } else {
            var diff = candidate.price - variant.price;
            if (diff > 0) { text = '+' + money(diff); tone = 'up'; }
            else if (diff < 0) { text = '−' + money(-diff); tone = 'down'; }
            else { text = 'Same price'; tone = 'same'; }
          }
          if (delta.textContent !== text) {
            delta.textContent = text;
            delta.setAttribute('data-tone', tone);
            replay(delta, 'is-fresh');
          }
        });
      });

      if (changed) setTitle(variant.title);
    }

    function setTitle(title) {
      if (!title) return;
      var section = document.getElementById('shopify-section-' + sectionId) || document;
      var targets = Array.prototype.slice.call(section.querySelectorAll('[data-wt-title]'));
      Array.prototype.push.apply(targets, document.querySelectorAll('#wattec-bar .wb-title, .sticky-bar .sticky-title'));
      targets.forEach(function (el) {
        if (el.textContent.trim() === title) return;
        el.textContent = title;
        replay(el, 'wt-title-swap');
      });
    }

    function replay(el, cls) {
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
    }

    // --- Choosing -------------------------------------------------------------
    function apply(variant) {
      if (!variant) return;
      var last = null;
      selects.forEach(function (select, k) {
        if (select.value !== variant.options[k]) {
          select.value = variant.options[k];
          last = select;
        }
      });
      show(variant);
      if (last) whenThemeReady(function () { last.dispatchEvent(new Event('change', { bubbles: true })); });
    }

    function choose(index, value) {
      apply(resolve(index, value, (shown || current()).options));
    }

    groups.forEach(function (group, k) {
      group.addEventListener('change', function (event) {
        var radio = event.target;
        if (!radio || !radio.classList.contains('wt-vp__radio')) return;
        var opt = radio.closest('.wt-vp__opt');
        if (opt) replay(opt, 'is-pressed');
        choose(k, radio.value);
      });
    });

    // Follow the cart form however its variant changes (sticky bar, other apps).
    if (idInput) {
      idInput.addEventListener('change', function () {
        var v = byId(idInput.value);
        if (v) show(v);
      });
    }
    native.addEventListener('change', function (event) {
      if (event.target && event.target.tagName === 'SELECT') {
        var v = byOptions(selects.map(function (s) { return s.value; }));
        if (v) show(v);
      }
    });

    native.classList.add('wt-vp-native');
    root.classList.add('is-ready');

    // Open on the variant the title describes when the link didn't pick one
    // (the title is already rendered for it, so it's shown straight away).
    var hasVariantParam = /[?&]variant=/.test(window.location.search);
    var match = byId(data.matchId);
    var initial = current();
    if (!hasVariantParam && match && match.available && initial && initial.id !== match.id) {
      show(match);
      apply(match);
    } else {
      show(initial);
    }
  }

  function start() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-wt-vp]'), init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
